import {Injectable, Injector} from '@angular/core';
import {isMobile} from '../common/utils/detect-user-agent';
import {HttpClient} from '@angular/common/http';
import {EnvironmentService} from './environment.service';
import {filter, switchMap, tap} from 'rxjs/operators';
import {IUiActionEvent} from '../common/models/interfaces';
import {interval} from 'rxjs';
import {UtmService} from '../auth/shared/services/utm.service';
import {UserInfoQuery} from '../common/elf-store/user-info/user-info.query';
import {UserInfoState} from '../common/elf-store/user-info/user-info.type';
import {GoogleTagManagerService} from 'angular-google-tag-manager';
import {AccountService} from './account.service';
import {PaginationService} from './pagination.service';
import {ApiClient} from './api/userway-api.service';
import {ILeadEvent} from '../auth/shared/services/activity-tracking.service';

type ITrackEvent = IUiActionEvent & ILeadEvent;

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  public trackingInterval: number = 5000;
  private bufferEvents: IUiActionEvent[] = [];
  private userInfo: UserInfoState;
  private accountService: AccountService;
  private userDataFromToken;

  constructor(
    private userInfoQuery: UserInfoQuery,
    private env: EnvironmentService,
    private http: HttpClient,
    private utmService: UtmService,
    private gtmService: GoogleTagManagerService,
    private injector: Injector,
    private paginationService: PaginationService,
    private apiClient: ApiClient,
  ) {
    setTimeout(() => {
      this.accountService = this.injector.get(AccountService);
    });
    this.collectUserData();
  }

  public pushGtmTag(tagData: {event: string; gtm_trigger: string; lead_type?: string; site?: string}) {
    const data = {...tagData, ...this.getAdditionalEventData()};
    return this.gtmService.pushTag(data).catch(() => {});
  }

  public trackEvent(data: Partial<ITrackEvent>): void {
    if (this.isDuplicatedEvent(data)) {
      return;
    }

    data.page = this.validatePage(data.page);
    const utmParams = this.utmService.getAndRemoveUtmIfAny();
    const event: IUiActionEvent = {
      event: data.event,
      timestamp: new Date().getTime(),
      content: data.content,
      page: data.page,
      device: isMobile() ? 'm' : 'd',
      ...utmParams,
      ...this.getAdditionalEventData(),
      ...(data.site && {site: data.site}), // data.site we get only when open site details drawer
      ...this.addPageAndTabToEvent(data),
      ...data,
    } as IUiActionEvent;

    this.addToBuffer(event);
  }

  public resetUserTokenData() {
    this.userDataFromToken = null;
  }

  private getAdditionalEventData() {
    if (!this.userDataFromToken && this.accountService) {
      this.userDataFromToken = this.accountService.getInfoFromJWT();
    }

    const isUserLoggedInByUwAdmin =
      this.userDataFromToken?.targetSsoUserId !== this.userDataFromToken?.authenticatedUserId;

    // When uw admin logged in by admin panel - set additional event values
    return isUserLoggedInByUwAdmin && {personate: true, userway_user_id: this.userDataFromToken?.authenticatedUserId};
  }

  private collectUserData(): void {
    this.userInfoQuery.userInfo$
      .pipe(
        tap(userInfo => (this.userInfo = userInfo)),
        switchMap(() => interval(this.trackingInterval)),
        filter(() => Boolean(this.bufferEvents.length)),
        switchMap(() => this.sendCollectedTrackingData()),
      )
      .subscribe();
  }

  private isDuplicatedEvent(eventData: Partial<IUiActionEvent>): boolean {
    const lastEvent = this.getLastTrackingEvent();

    if (!lastEvent) {
      return false;
    }

    return (
      lastEvent.page === eventData.page &&
      lastEvent.event === eventData.event &&
      lastEvent.content === eventData.content
    );
  }

  private sendCollectedTrackingData() {
    const data = {
      events: this.bufferEvents.map(item => {
        item.uId = this.userInfo.id ?? null;
        return item;
      }),
    };

    this.resetBuffer();

    const request = this.apiClient.api.misc.abnManageAdmCreate(data);
    return this.apiClient.fetch(request);
  }

  private getLastTrackingEvent() {
    const lastEventIndex = this.bufferEvents.length - 1;
    return this.bufferEvents[lastEventIndex];
  }

  private addToBuffer(data: IUiActionEvent): void {
    this.bufferEvents.push(data);
  }

  private resetBuffer(): void {
    this.bufferEvents = [];
  }

  private validatePage(page: string) {
    let pageUrl = page || window.location.pathname;

    if (pageUrl === '/widget/my-sites/p/1') {
      pageUrl = '/widget';
    }
    return pageUrl;
  }

  private addPageAndTabToEvent(data: Partial<IUiActionEvent>) {
    let payload: {tab?: string; page_number?: number} = {};

    if (data.page === '/widget') {
      payload.tab = this.paginationService.currentTab;
      payload.page_number = this.paginationService.currentPage;
    }

    return payload;
  }
}
